import { GridApi, IRowNode, ServerSideTransaction } from 'ag-grid-community';
import { Subject } from 'rxjs';
import { GridRefreshOptions } from './models/grid-refresh-options';

export abstract class GridTransactionService<TRow extends { rowId: string }> {
  public static readonly ROUTE_SEPARATOR = '_';

  protected gridRefresh$ = new Subject<GridRefreshOptions | undefined>(); // Route to refresh. Undefined options = refresh all loaded rows
  protected gridUpdate$ = new Subject<ServerSideTransaction>();
  protected api?: GridApi<TRow>;
  protected currentTransaction: Omit<Required<ServerSideTransaction>, 'addIndex'> = this.getEmptyTransaction();

  public initialize(gridApi: GridApi) {
    this.api = gridApi;
    this.currentTransaction = this.getEmptyTransaction();
  }

  public applyTransaction(transaction: ServerSideTransaction) {
    this.api?.applyServerSideTransactionAsync(transaction);
  }

  public observeGridTransaction() {
    return this.gridUpdate$.asObservable();
  }

  public observeGridRefresh() {
    return this.gridRefresh$.asObservable();
  }

  public pushTransaction =
    (action: 'add' | 'update' | 'remove') =>
    (rows: TRow[], route?: string[], flushTransaction = true) => {
      // Auto-determine parent route from rowId if not provided. Must be provided for 'add'.
      route ??= this.getRowNodeFromCache(rows[0]?.rowId)?.parent?.getRoute() ?? [];

      if (route?.join() !== this.currentTransaction.route?.join()) {
        this.flushCurrentTransaction(); // flush current trx if route changes (a trx can only have one route)
      }
      this.currentTransaction[action]?.push(...rows);
      this.currentTransaction.route = route ?? [];
      if (flushTransaction) {
        this.flushCurrentTransaction();
      }
    };

  public addRows = this.pushTransaction('add');
  public updateRows = this.pushTransaction('update');
  public removeRows = this.pushTransaction('remove');

  public flushCurrentTransaction() {
    const { add, update, remove } = this.currentTransaction;
    if (!add.length && !update.length && !remove.length) return;

    this.gridUpdate$.next(this.currentTransaction);
    this.currentTransaction = this.getEmptyTransaction();
  }

  private getEmptyTransaction() {
    return { route: [], update: [], add: [], remove: [] };
  }

  public getRowNodeFromCache<T extends TRow = TRow>(rowId: string) {
    return this.api?.getRowNode(rowId) as IRowNode<T> | undefined;
  }

  public getRowFromCache<T extends TRow = TRow>(rowId: string) {
    return this.getRowNodeFromCache(rowId)?.data as T | undefined;
  }

  /**
   * Ask for a grid refresh. Grid services should subscribe to it to refresh their loaded rows.
   */
  public requestGridRefresh(options?: GridRefreshOptions) {
    this.gridRefresh$.next(options);
  }

  /**
   * Refresh all loaded rows in the grid.
   * If childRoute is provided, only group levels that are parents of the child route will be refreshed.
   * eg. childRoute: ['Athletes', 'Greece'] will refresh only the following groups: [], ['Athletes'] and ['Athletes', 'Greece'].
   */
  public refreshLoadedRows(childRoute?: string[]) {
    this.api?.flushServerSideAsyncTransactions();
    const groupState = this.api?.getServerSideGroupLevelState();
    const sep = GridTransactionService.ROUTE_SEPARATOR;
    groupState?.forEach(({ route }) => {
      if (childRoute && !childRoute.join(sep).startsWith(route.join(sep))) return;
      this.api?.refreshServerSide({ route, purge: false });
    });
  }
}
